import type { Stat } from '../../components/charts/StatTiles'
import { exactNumber } from '../../components/charts/scale'
import type { FleetCoverage, SeatLimit } from '../../lib/api'

/**
 * The five figures at the top of the fleet list, decided as data rather than as markup.
 *
 * Every one of them is counted by the server over the whole fleet, and that is the constraint this
 * module exists to hold. The obvious way to build a ribbon here is to reduce over the rows already in
 * the browser - the page knows each device's check-in and envelope version, so counting them looks
 * free. It would have reported fifty of nine hundred and eighty-seven as though it were the fleet:
 * the precise shape of D-263, committed in the panel added to make the fleet legible.
 *
 * `/api/v1/coverage` answers all of it already, fleet-wide, for the strip the shell has carried on
 * every screen since the redesign. Reading it here is not a second measurement, it is the same one
 * shown where somebody is actually looking at devices.
 *
 * Null therefore means the read did not arrive and renders as a dash with no note. It never renders
 * as a zero, and it never carries a tone: an all-clear printed at the moment the page knows least is
 * the failure `fleetSummary` was written for, and repeating it here would be repeating it knowingly.
 */
export interface FleetRibbonSource {
  coverage: FleetCoverage | null
  seats: SeatLimit | null
}

/** The dash used wherever a number is not known. Not a zero, and not an empty cell. */
export const NOT_MEASURED = '—'

export function fleetRibbon(source: FleetRibbonSource): Stat[] {
  const { coverage, seats } = source

  // "Not reached" rather than "offline", because that is what was measured: the device has not
  // completed a check-in inside the server's reporting window. A machine that is switched on and
  // publishing telemetry every minute is in this number if it has not polled the feed, and that is
  // the whole distinction the fleet screen exists to make visible.
  const unreached = coverage === null ? null : Math.max(0, coverage.devicesEnrolled - coverage.devicesReporting)
  const hours = coverage?.reportingWindowHours ?? null

  return [
    {
      label: 'Enrolled',
      value: coverage === null ? NOT_MEASURED : exactNumber(coverage.devicesEnrolled),
      note:
        seats === null
          ? undefined
          : `${exactNumber(seats.remaining)} of ${exactNumber(seats.seatLimit)} seats left`
    },
    {
      label: 'Not reached',
      value: unreached === null ? NOT_MEASURED : exactNumber(unreached),
      note: hours === null ? undefined : `No check-in in ${describeWindow(hours)}`,
      // Toned only when something is actually wrong, and only when it was actually measured.
      tone: unreached !== null && unreached > 0 ? 'warning' : 'plain'
    },
    {
      label: 'Carrying no policy',
      value: coverage === null ? NOT_MEASURED : exactNumber(coverage.devicesCarryingNoPolicy),
      note: 'Enrolled, and running nothing',
      tone: coverage !== null && coverage.devicesCarryingNoPolicy > 0 ? 'danger' : 'plain',
      to: '/policies'
    },
    {
      // Three answers, not two. A device that has never submitted a chain is not a device whose chain
      // failed - it is a device no audit policy applies to - so it is the note rather than part of the
      // figure, exactly as the shell's coverage bar keeps them apart.
      label: 'Log broken',
      value: coverage === null ? NOT_MEASURED : exactNumber(coverage.auditChainsBroken),
      note:
        coverage === null
          ? undefined
          : `${exactNumber(coverage.auditChainsVerified)} verified, ${exactNumber(coverage.auditChainsAbsent)} never submitted one`,
      tone: coverage !== null && coverage.auditChainsBroken > 0 ? 'danger' : 'plain'
    }
  ]
}

/** The window in the words somebody would say it in, from the number the server sent. */
function describeWindow(hours: number): string {
  if (hours === 24) return 'the last day'
  if (hours === 1) return 'the last hour'
  if (hours % 24 === 0) return `the last ${hours / 24} days`
  return `the last ${hours} hours`
}
